import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { ProductSupplierQuoteService } from '@app/api/productSupplierQuote.service';
import { ProductService } from '@app/api/product.service';
import {
  ProductSupplierQuote,
  formatQuantity,
  GetProductSupplierQuotesParams
} from '@app/model/productSupplierQuote';
import {
  ModalSupplierQuoteComponent,
  ModalSupplierQuoteData
} from '../modal-supplier-quote/modal-supplier-quote.component';

import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'meu-app-product-supplier-quotes',
  templateUrl: './app-product-supplier-quotes.component.html',
  styleUrls: ['./app-product-supplier-quotes.component.scss']
})
export class AppProductSupplierQuotesComponent implements OnInit, OnChanges {
  @Input() productId: string;
  @Input() productData: any;
  @Input() initialQuotes?: ProductSupplierQuote[];
  @Output() countChange: EventEmitter<number> = new EventEmitter<number>();

  quotes: ProductSupplierQuote[] = [];
  loading = false;
  selectedProductTypeId = ''; // '' means all
  variants: Array<{ id: string; name: string }> = [];

  // Pagination (MatPaginator is 0-indexed)
  pageIndex = 0;
  pageSize = 10;
  totalCount = 0;
  pageSizeOptions: number[] = [5, 10, 20, 50];

  // Search & Filter (supplier_name@=, quote_time>=)
  searchSupplierName = '';
  filterQuoteTimeFrom = '';

  constructor(
    private quoteService: ProductSupplierQuoteService,
    private productService: ProductService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private layoutUtilsService: LayoutUtilsService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.extractVariants();
    if (this.variants.length === 0 && this.productId) {
      this.loadProductDetails();
    }
    if (this.initialQuotes && Array.isArray(this.initialQuotes) && this.initialQuotes.length > 0) {
      this.quotes = [...this.initialQuotes];
      this.totalCount = this.initialQuotes.length;
      this.countChange.emit(this.totalCount);
    } else if (this.productId) {
      this.loadQuotes();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.productData && changes.productData.currentValue) {
      this.extractVariants();
    }
    if (changes.productId && changes.productId.currentValue && !changes.productId.firstChange) {
      this.loadQuotes();
    }
    if (changes.initialQuotes && changes.initialQuotes.currentValue) {
      this.quotes = [...changes.initialQuotes.currentValue];
      this.totalCount = this.quotes.length;
      this.countChange.emit(this.totalCount);
    }
  }

  /**
   * Load product details if variants are not passed by parent component
   */
  loadProductDetails(): void {
    if (!this.productId) return;
    this.productService.apiProductsIdGet(this.productId).subscribe(
      (res) => {
        if (res && res.success && res.data) {
          this.productData = Object.assign({}, this.productData || {}, res.data);
          this.extractVariants();
          this.cdr.detectChanges();
        }
      },
      (err) => {
        console.error('[AppProductSupplierQuotes] loadProductDetails error', err);
      }
    );
  }

  /**
   * Extract list of variants from productData for filter and modal
   */
  extractVariants(): void {
    if (!this.productData) return;

    const list: Array<{ id: string; name: string }> = [];

    const rawTypes =
      this.productData.extend_product_type ||
      this.productData.type_values ||
      [];

    // From extend_product_type/type_values and extend_type
    if (Array.isArray(rawTypes) && rawTypes.length > 0) {
      const typeLookup = new Map<string, string>();
      if (this.productData.extend_type && Array.isArray(this.productData.extend_type)) {
        this.productData.extend_type.forEach((group: any) => {
          if (group && group.types && Array.isArray(group.types)) {
            group.types.forEach((t: any) => {
              if (t && t.id) {
                const label = group.group_name ? `${group.group_name}: ${t.name}` : t.name;
                typeLookup.set(t.id, label);
              }
            });
          }
        });
      }

      rawTypes.forEach((pt: any) => {
        if (!pt || !pt.id) return;
        let displayName = pt.name || pt.product_type_text || '';
        if (!displayName && pt.types && Array.isArray(pt.types)) {
          displayName = pt.types
            .map((t: any) => t.name)
            .filter((n: any) => !!n)
            .join(' - ');
        }
        if (!displayName && pt.type_id && Array.isArray(pt.type_id)) {
          const names = pt.type_id
            .map((tid: string) => typeLookup.get(tid))
            .filter((n: any) => !!n);
          if (names.length > 0) {
            displayName = names.join(' - ');
          }
        }
        if (!displayName) {
          displayName = this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.VARIANT_FALLBACK', {
            index: list.length + 1
          });
        }
        list.push({ id: pt.id, name: displayName });
      });
    }

    this.variants = list;
  }

  /**
   * Load supplier quotes with pagination, variant filter, and search
   */
  loadQuotes(showLoading = true): void {
    if (!this.productId) return;
    if (showLoading) {
      this.loading = true;
    }

    const params: GetProductSupplierQuotesParams = {
      page: this.pageIndex + 1, // Sieve page is 1-based
      pageSize: this.pageSize,
      productTypeId: this.selectedProductTypeId || undefined,
      supplier_name: this.searchSupplierName ? this.searchSupplierName.trim() : undefined,
      quote_time: this.filterQuoteTimeFrom ? this.filterQuoteTimeFrom.trim() : undefined
    };

    this.quoteService.getProductSupplierQuotes(this.productId, params).subscribe(
      (res: any) => {
        this.loading = false;
        if (res && res.success && res.data) {
          if (Array.isArray(res.data)) {
            this.quotes = res.data;
            this.totalCount = res.data.length;
          } else if (res.data.collection) {
            this.quotes = res.data.collection || [];
            this.totalCount = res.data.total != null ? res.data.total : this.quotes.length;
            if (res.data.pageIndex) {
              this.pageIndex = res.data.pageIndex - 1;
            }
            if (res.data.pagesize) {
              this.pageSize = res.data.pagesize;
            }
          } else {
            this.quotes = [];
            this.totalCount = 0;
          }
          this.countChange.emit(this.totalCount);
        } else {
          this.quotes = [];
          this.totalCount = 0;
          this.countChange.emit(0);
        }
        this.cdr.detectChanges();
      },
      (err) => {
        this.loading = false;
        console.error('[AppProductSupplierQuotes] load error', err);
        this.cdr.detectChanges();
      }
    );
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.loadQuotes();
  }

  onSearch(): void {
    this.pageIndex = 0;
    this.loadQuotes();
  }

  onResetSearch(): void {
    this.searchSupplierName = '';
    this.filterQuoteTimeFrom = '';
    this.selectedProductTypeId = '';
    this.pageIndex = 0;
    this.loadQuotes();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadQuotes();
  }

  hasSearchFilters(): boolean {
    return !!(
      (this.searchSupplierName && this.searchSupplierName.trim()) ||
      this.filterQuoteTimeFrom ||
      this.selectedProductTypeId
    );
  }

  openAddModal(): void {
    const dialogData: ModalSupplierQuoteData = {
      productId: this.productId,
      productName: this.productData ? this.productData.name : '',
      variants: this.variants
    };

    const dialogRef = this.dialog.open(ModalSupplierQuoteComponent, {
      data: dialogData,
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.saved) {
        this.loadQuotes(false);
      }
    });
  }

  openEditModal(quote: ProductSupplierQuote): void {
    const dialogData: ModalSupplierQuoteData = {
      productId: this.productId,
      productName: this.productData ? this.productData.name : '',
      variants: this.variants,
      quote
    };

    const dialogRef = this.dialog.open(ModalSupplierQuoteComponent, {
      data: dialogData,
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.saved) {
        this.loadQuotes(false);
      }
    });
  }

  deleteQuote(quote: ProductSupplierQuote): void {
    const title = this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.DELETE_CONFIRM_TITLE');
    const description = this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.DELETE_CONFIRM_DESC', {
      supplier: quote.supplier_name
    });
    const waitDescription = this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.DELETE_WAIT');

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      this.quoteService.deleteSupplierQuote(quote.id).subscribe(
        (delRes) => {
          if (delRes && delRes.success) {
            const successMsg =
              delRes.message || this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.DELETE_SUCCESS');
            this.toastr.success(successMsg);
            this.loadQuotes(false);
          } else {
            const errorMsg =
              (delRes && delRes.message) ||
              this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.DELETE_ERROR');
            this.toastr.error(errorMsg);
          }
        },
        (err) => {
          console.error('[AppProductSupplierQuotes] delete error', err);
          const msg =
            (err && err.error && err.error.message) ||
            this.translate.instant('ECOMMERCE.SUPPLIER_QUOTES.DELETE_ERROR');
          this.toastr.error(msg);
        }
      );
    });
  }

  /**
   * Format quantity: integer if whole number, decimal if fractional
   */
  formatQty(qty: number | null | undefined): string {
    return formatQuantity(qty);
  }

  trackById(index: number, item: ProductSupplierQuote): string {
    return item.id;
  }
}
